import React, { useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { RegistrationProvider, useRegistration } from "../context/RegistrationContext";
import {
  FluentProvider,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { sourcestoneLightTheme } from "../theme";
import Header from "./Header";
import { LoginPage } from "../pages/LoginPage";
import { TaskSelectionPage } from "../pages/TaskSelectionPage";
import { FileSelectionPage } from "../pages/FileSelectionPage";
import { BenSpecFileSelectionPage } from "../pages/BenSpecFileSelectionPage";
import { BenSpecQuestionsPage } from "../pages/BenSpecQuestionsPage";
import { BenSpecConfirmationPage } from "../pages/BenSpecConfirmationPage";
import { JobProvider } from "../context/JobContext";
import { MemberSelectionPage } from "../pages/MemberSelectionPage";
import { FieldsSelectionPage } from "../pages/FieldsSelectionPage";
import { ConfirmationPage } from "../pages/ConfirmationPage";
import { DashboardPage } from "../pages/DashboardPage";
import { MemberDataReportOptionsPage } from "../pages/MemberDataReportOptionsPage";
import { SetupPage } from "../pages/SetupPage";
import { AdminStorageSetupPage } from "../pages/AdminStorageSetupPage";
import PdfViewer from "./PdfViewer";
import { getAccessToken } from "../services/authService";
import { fetchGraphPdfAsArrayBuffer } from "../services/graphProxyService";
import { useIdleTimer } from "../hooks/useIdleTimer";
import { IdleWarningDialog } from "./IdleWarningDialog";

declare global {
  interface Window {
    SS_invalidateMetaIndex?: () => void;
  }
}

const useAppStyles = makeStyles({
  provider: {
    "& .sourcestone-navigation-primary": {
      color: "#ffffff",
      backgroundColor: "#0f6cbd",
      ...shorthands.border("1px", "solid", "#0f6cbd"),
      borderRadius: "8px",
      boxShadow: "none",
      ":hover": {
        color: "#ffffff",
        backgroundColor: "#115ea3",
        ...shorthands.borderColor("#115ea3"),
      },
      ":active": {
        color: "#ffffff",
        backgroundColor: "#0c3b5e",
        ...shorthands.borderColor("#0c3b5e"),
      },
      ":disabled": {
        color: "#ffffff",
        backgroundColor: "#0f6cbd",
        ...shorthands.borderColor("#0f6cbd"),
        opacity: 0.45,
      },
    },
  },
});

/* global require */
// Define the pages that can be displayed
export type Page =
  | "login"
  | "taskSelection"
  | "setup"
  | "adminStorageSetup"
  | "fileSelection"
  | "benSpecFileSelection"
  | "benSpecQuestions"
  | "benSpecConfirmation"
  | "memberSelection"
  | "fieldSelection"
  | "confirmation"
  | "dashboard"
  | "memberReportOptions";

// (No demo constants needed in production flow)

// (No helper for combined IDs needed in production flow)

function parseDocCell(raw: string): { docKey: string; page: number } | null {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!/^doc:?\s*/i.test(value)) return null;

  const withoutPrefix = value.replace(/^doc:?\s*/i, "");
  const cleanDocKey = (s: string) => s.replace(/[\s,]+$/g, "").trim();

  // Supported formats (case/space/colon tolerant):
  // - "Doc: Title Page 7" or "Doc: Title Page: 7"
  // - "Doc: Title#7"
  // - "Doc: Title" (defaults to page 1)

  // Regex 1: "#" separator
  let m = withoutPrefix.match(/^(.+?)\s*#\s*(\d+)\s*$/i);
  if (m) {
    return { docKey: cleanDocKey(m[1]), page: Number(m[2]) || 1 };
  }

  // Regex 2: "page" separator
  // Use greedy match (.*) for the first part to ensure we match the *last* "page" occurrence
  // This prevents splitting on "page" inside a filename (e.g. "FrontPage.pdf, Page: 1")
  m = withoutPrefix.match(/^(.*)[\s,]+page:?\s*(\d+)\s*$/i);
  if (m) {
    return { docKey: cleanDocKey(m[1]), page: Number(m[2]) || 1 };
  }

  const docKey = cleanDocKey(withoutPrefix);
  if (!docKey) return null;
  return { docKey, page: 1 };
}

function normalizeDocKey(raw: string): string {
  return String(raw || "")
    .trim()
    .replace(/[\s,]+$/g, "") // strip trailing spaces/commas
    .toLowerCase();
}

// removed test helpers; robust parseDocCell covers detection

const AppContent = () => {
  const isAuthenticated = useIsAuthenticated();
  const [currentPage, setCurrentPage] = useState<Page>("taskSelection");
  const [pdfViewerPage, setPdfViewerPage] = useState<number>(1);
  const { instance } = useMsal();
  const { state: registrationState, refresh: refreshRegistration } = useRegistration();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const lastPageRef = React.useRef<Page>("taskSelection");
  const [pdfError, setPdfError] = useState<string | null>(null); // Tracks viewer errors so we can inform the user via dialog.
  const selectionHandlerRef = React.useRef<((args?: any) => void) | null>(null);
  const metaIndexRef = React.useRef<Record<string, any> | null>(null);

  // ── Inactivity auto-logout ────────────────────────────────────────────
  // Monitors user activity and shows a warning dialog before logging out.
  // Only active when the user is authenticated; disabled on the login page.
  const { isWarning, remainingSeconds, resetIdleTimer } = useIdleTimer({
    enabled: isAuthenticated,
  });

  const handleSignOut = async () => {
    // 1. Flag the next login attempt so we trigger a full credential prompt.
    // This prevents the user from just clicking "Login" and getting back in via SSO.
    try {
      localStorage.setItem("SS_REQUIRE_REAUTH", "1");
    } catch (error) {
      console.error("Failed to set reauth flag:", error);
    }

    // 2. Clear the local session by reloading the task pane.
    // Since cacheLocation is "memoryStorage", a reload wipes the tokens.
    // This avoids the "Which user would you like to sign out of?" popup entirely.
    window.location.reload();
  };

  const closePdfViewer = (nextPage?: Page) => {
    // Ensure the PDF blob URL is released and the viewer state is reset.
    setPdfUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return undefined;
    });
    // Reset viewer state so it does not block navigation.
    setPdfViewerPage(1);
    setIsPdfLoading(false);
    setPdfError(null);
    // Navigate to a requested page or return to the last non-viewer page.
    if (nextPage) {
      lastPageRef.current = nextPage;
      setCurrentPage(nextPage);
      return;
    }
    setCurrentPage(lastPageRef.current);
  };

  const handleGoToDashboard = () => {
    // Force-close the viewer so the dashboard can render.
    closePdfViewer("dashboard");
  };

  // Build and cache an index of SS_META keyed by docKey -> { driveId, itemId, fileUrl }
  async function loadMetaIndex(): Promise<Record<string, any>> {
    if (metaIndexRef.current) {
      return metaIndexRef.current;
    }

    let index: Record<string, any> = {};
    await Excel.run(async (context) => {
      try {
        const sheet = context.workbook.worksheets.getItemOrNullObject("_SS_META");
        sheet.load("name");
        await context.sync();
        const notFound = (sheet as any).isNullObject;
        if (notFound) {
          console.error("[PDF Viewer] Metadata sheet not found.");
          index = {};
          return;
        }

        const used = sheet.getUsedRange(true);
        used.load("values");
        await context.sync();

        const values = (used as any).values as any[][];
        if (!values || values.length < 2) {
          console.warn("[PDF Viewer] Metadata sheet is empty.");
          index = {};
          return;
        }
        // Normalize header names: lowercase and strip non-alphanumerics for flexible matching
        const norm = (s: string) =>
          String(s || "")
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");
        const [header, ...rows] = values;
        const normalized = header.map((h: string) => norm(h));
        const findColByNames = (candidates: string[]): number => {
          for (const c of candidates) {
            const n = norm(c);
            const i = normalized.indexOf(n);
            if (i >= 0) return i;
          }
          return -1;
        };

        const keyIdx = findColByNames([
          "docKey",
          "doc_key",
          "dockey",
          "referenceKey",
          "reference",
          "refKey",
          "docname",
          "name",
          "filename",
          "file",
        ]);
        const driveIdx = findColByNames(["driveId", "drive_id", "drive"]);
        const itemIdx = findColByNames(["itemId", "item_id", "item"]);
        const webIdxCandidate = findColByNames(["webUrl", "web_url", "web", "url"]);
        const shareIdxCandidate = findColByNames(["shareUrl", "share_url", "sharelink", "link"]);
        const webIdx = webIdxCandidate >= 0 ? webIdxCandidate : shareIdxCandidate;

        const safe = (r: any[], i: number) => (i >= 0 ? String(r[i] ?? "").trim() : "");

        for (const r of rows) {
          const k = normalizeDocKey(safe(r, keyIdx));
          if (!k) continue;
          index[k] = {
            driveId: safe(r, driveIdx),
            itemId: safe(r, itemIdx),
            fileUrl: safe(r, webIdx),
          };
        }
      } catch (e: any) {
        console.error("[PDF Viewer] Failed to read metadata sheet.", e);
      }
    });

    // Only cache when we found real entries.
    // If _SS_META was missing or empty (e.g. workbook still loading, or user
    // clicked before a report was open), leave the cache null so the next
    // click retries rather than permanently returning an empty lookup.
    if (Object.keys(index).length > 0) {
      metaIndexRef.current = index;
    }
    return index;
  }

  // (Debug snapshot writer removed for production)

  const invalidateMetaIndex = React.useCallback(() => {
    metaIndexRef.current = null;
  }, []);

  useEffect(() => {
    window.SS_invalidateMetaIndex = invalidateMetaIndex;

    return () => {
      if (window.SS_invalidateMetaIndex === invalidateMetaIndex) {
        delete window.SS_invalidateMetaIndex;
      }
    };
  }, [invalidateMetaIndex]);

  // CSP reporting probe (temporary) - DISABLED:
  // We used this to intentionally trigger a harmless CSP violation after login to validate reporting.
  // Commented out so it doesn't generate noise during normal usage.
  //
  // To re-enable, uncomment the effect below.
  //
  // useEffect(() => {
  //     if (!isAuthenticated) {
  //         return;
  //     }
  //     if (cspProbeTriggeredRef.current) {
  //         return;
  //     }
  //
  //     cspProbeTriggeredRef.current = true;
  //     try {
  //         (window as any).SS_triggerCspProbe?.();
  //     } catch {
  //         // Never allow diagnostics to break the task pane.
  //     }
  // }, [isAuthenticated]);

  // Register Excel worksheet click handlers once authenticated.
  //
  // Why we register both:
  // - `workbook.onSelectionChanged` only fires when the selection *changes*.
  // - If the user clicks the *already selected* cell again, selection does not change, so our PDF
  //   open logic never runs.
  // - `worksheet.onSingleClicked` (ExcelApi 1.10) fires on left-click/tap even if selection stays the same.
  // - However, `onSingleClicked` can be unreliable (e.g. if the mouse moves slightly, or on external monitors).
  // - We register both and deduplicate them so we get the reliability of `onSelectionChanged`
  //   and the ability to catch same-cell clicks from `onSingleClicked`.
  useEffect(() => {
    if (!isAuthenticated) return () => {};

    const register = async () => {
      await Office.onReady();
      const fetchPdfData = async (graphPayload: {
        drive_id?: string;
        item_id?: string;
        file_url?: string;
      }) => {
        // Always acquire a current token for viewer opens so long-lived task pane sessions
        // do not keep using a bearer minted much earlier in the session.
        let token = await getAccessToken(instance);

        try {
          return await fetchGraphPdfAsArrayBuffer(graphPayload, token);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (!message.includes("401")) {
            throw error;
          }

          // A request can still race into expiry at the backend boundary, so force one
          // refresh and retry once before surfacing a sign-in error to the user.
          token = await getAccessToken(instance, { forceRefresh: true });
          return await fetchGraphPdfAsArrayBuffer(graphPayload, token);
        }
      };

      // Resolve a doc key against the _SS_META index with extension-strip and
      // full-name fallbacks.  Returns the meta entry or null.
      const lookupMetaEntry = async (docKey: string, rawCellValue: string): Promise<any | null> => {
        const meta = await loadMetaIndex();
        const key = normalizeDocKey(docKey);
        let entry = (meta as any)[key] ?? null;

        if (!entry) {
          // Fallback 1: strip file extension ("File.pdf" → "File")
          const dotIndex = docKey.lastIndexOf(".");
          if (dotIndex > 0) {
            const noExtKey = normalizeDocKey(docKey.substring(0, dotIndex));
            entry = (meta as any)[noExtKey] ?? null;
          }
        }

        if (!entry) {
          // Fallback 2: the filename might contain "#" or "Page" which the
          // regex mistook for a separator — try the full raw name.
          const rawName = rawCellValue.trim().replace(/^doc:?\s*/i, "");
          const fallbackKey = normalizeDocKey(rawName);
          entry = (meta as any)[fallbackKey] ?? null;
          if (entry) {
            entry = { ...entry, __fallbackFullName: true };
          }
        }

        return entry;
      };

      let lastProcessedEvent = { key: "", time: 0 };

      // Main "open PDF for current selection" routine with debug logging.
      // Reads both getSelectedRange() and the event args cell to detect mismatches.
      // If the selected range doesn't contain a "Doc:" value but the event args cell does,
      // we fall back to the event cell (handles onSingleClicked timing race).
      const handleCellInteraction = async (
        source: "singleClick" | "selectionChanged",
        eventArgs?: any
      ) => {
        if (eventArgs?.address) {
          console.log(`[PDF Viewer] ${source} at ${eventArgs.address}`);
        } else {
          console.log(`[PDF Viewer] ${source} triggered`);
        }

        try {
          let selectedValue = "";
          let eventCellValue = "";
          let activeAddress = "";
          let activeWorksheetId = "";

          await Excel.run(async (context) => {
            const range = context.workbook.getSelectedRange();
            const activeWorksheet = context.workbook.worksheets.getActiveWorksheet();
            range.load(["address", "values", "text"]);
            activeWorksheet.load(["id"]);

            // Also read from event args cell address for comparison/fallback
            let eventRange: any = null;
            if (eventArgs?.address) {
              try {
                const ws = eventArgs.worksheetId
                  ? context.workbook.worksheets.getItem(eventArgs.worksheetId)
                  : context.workbook.worksheets.getActiveWorksheet();
                eventRange = ws.getRange(eventArgs.address);
                eventRange.load(["address", "values", "text"]);
              } catch (rangeErr) {
                console.warn("[PDF Viewer] Could not read clicked cell range.", rangeErr);
              }
            }

            await context.sync();

            activeAddress = range.address;
            activeWorksheetId = activeWorksheet.id;
            const displayText = (range as any).text?.[0]?.[0];
            selectedValue = String(displayText ?? range.values?.[0]?.[0] ?? "");

            if (eventRange) {
              try {
                const evtText = eventRange.text?.[0]?.[0];
                eventCellValue = String(evtText ?? eventRange.values?.[0]?.[0] ?? "");
              } catch (evtErr) {
                console.warn("[PDF Viewer] Could not read clicked cell value.", evtErr);
              }
            }
          });

          // Deduplication logic
          const addressToDedup = eventArgs?.address || activeAddress;
          const worksheetIdToDedup = eventArgs?.worksheetId || activeWorksheetId;
          const eventKey = `${worksheetIdToDedup}:${addressToDedup}`;
          const now = Date.now();

          if (lastProcessedEvent.key === eventKey && now - lastProcessedEvent.time < 300) {
            console.log(`[PDF Viewer] skipped duplicate event for ${eventKey}`);
            return;
          }
          lastProcessedEvent = { key: eventKey, time: now };

          let valueToUse = selectedValue;
          let usingEventFallback = false;
          const parsed = parseDocCell(selectedValue);

          // If selectedRange didn't parse but the clicked cell does, use it as fallback
          if (!parsed && eventCellValue && eventCellValue !== selectedValue) {
            const eventParsed = parseDocCell(eventCellValue);
            if (eventParsed) {
              valueToUse = eventCellValue;
              usingEventFallback = true;
            }
          }

          const finalParsed = usingEventFallback ? parseDocCell(valueToUse) : parsed;
          if (!finalParsed) {
            return;
          }

          // Attempt SS_META lookup (with one cache-bust retry).
          let entry = await lookupMetaEntry(finalParsed.docKey, valueToUse);
          let pageToUse = finalParsed.page;

          if (!entry && metaIndexRef.current) {
            metaIndexRef.current = null;
            entry = await lookupMetaEntry(finalParsed.docKey, valueToUse);
          }

          // Fallback may have resolved via full raw name - reset page if so.
          if (entry && (entry as any).__fallbackFullName) {
            pageToUse = 1;
          }

          if (!entry) {
            console.error(
              "[PDF Viewer] Doc link found in cell but no matching _SS_META entry. Index size: %d",
              Object.keys(metaIndexRef.current || {}).length
            );
            return;
          }

          const graphPayload =
            entry.driveId && entry.itemId
              ? { drive_id: entry.driveId, item_id: entry.itemId }
              : entry.fileUrl
                ? { file_url: entry.fileUrl }
                : null;

          if (!graphPayload) {
            console.error(
              "[PDF Viewer] _SS_META entry found but contains no usable identifiers (driveId/itemId or fileUrl)."
            );
            return;
          }

          const targetPage = Math.max(1, pageToUse);
          setPdfError(null);
          setIsPdfLoading(true);
          const data = await fetchPdfData(graphPayload);

          const blob = new Blob([data], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          setPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
          setPdfViewerPage(targetPage);
          console.log("[PDF Viewer] Opened successfully for clicked cell.");
        } catch (e) {
          console.error("[PDF Viewer] Failed to open PDF for clicked cell.", e);
          const message = e instanceof Error ? e.message : String(e);
          const isUnauthorized = message?.includes("401");
          const isForbidden = message?.includes("403");
          const isNotFound = message?.includes("404") || message?.includes("400");
          if (isUnauthorized) {
            setPdfError(
              "We couldn’t open the PDF because your session expired. Please sign in again and retry the link."
            );
          } else if (isForbidden) {
            setPdfError(
              "We don’t have permission to open this PDF. Confirm you still have access to the document in SharePoint and try again."
            );
          } else if (isNotFound) {
            setPdfError(
              "We couldn’t locate that PDF. It may have been moved or you may no longer have access."
            );
          } else {
            setPdfError("Something went wrong while loading the PDF. Please try again.");
          }
        } finally {
          setIsPdfLoading(false);
        }
      };

      // Wrap the handlers to inject the source
      const onSingleClickHandler = (args: any) => handleCellInteraction("singleClick", args);
      const onSelectionChangedHandler = (args: any) =>
        handleCellInteraction("selectionChanged", args);

      // This ref is used for cleanup (remove handler) on unmount.
      selectionHandlerRef.current = {
        onSingleClick: onSingleClickHandler,
        onSelectionChanged: onSelectionChangedHandler,
      } as any;

      const supportsSingleClick =
        typeof Office !== "undefined" &&
        Office.context?.requirements?.isSetSupported?.("ExcelApi", "1.10") === true;

      await Excel.run(async (context) => {
        // Always register onSelectionChanged as the reliable baseline
        context.workbook.onSelectionChanged.add(onSelectionChangedHandler as any);

        // Also register onSingleClicked if supported (to catch clicking already-selected cells)
        if (supportsSingleClick) {
          (context.workbook.worksheets as any).onSingleClicked.add(onSingleClickHandler as any);
        }
        await context.sync();
      });
    };

    void register(); // Fire-and-forget: cleanup below detaches the handler directly.
    return () => {
      const handlers: any = selectionHandlerRef.current;
      if (handlers) {
        Excel.run(async (context) => {
          const supportsSingleClick =
            typeof Office !== "undefined" &&
            Office.context?.requirements?.isSetSupported?.("ExcelApi", "1.10") === true;

          context.workbook.onSelectionChanged.remove(handlers.onSelectionChanged);

          if (supportsSingleClick && handlers.onSingleClick) {
            (context.workbook.worksheets as any).onSingleClicked.remove(handlers.onSingleClick);
          }
          await context.sync();
        }).catch(() => {});
        selectionHandlerRef.current = null;
      }
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return undefined;
      });
    };
  }, [isAuthenticated, instance]);

  const navigate = (next: Page) => {
    lastPageRef.current = next;
    setCurrentPage(next);
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case "taskSelection":
        return <TaskSelectionPage onNavigate={navigate} />;
      case "setup":
        return <SetupPage onNavigate={navigate} />;
      case "adminStorageSetup":
        return <AdminStorageSetupPage onNavigate={navigate} />;
      case "fileSelection":
        return <FileSelectionPage onNavigate={navigate} />;
      case "benSpecFileSelection":
        return <BenSpecFileSelectionPage onNavigate={navigate} />;
      case "benSpecQuestions":
        return <BenSpecQuestionsPage onNavigate={navigate} />;
      case "benSpecConfirmation":
        return <BenSpecConfirmationPage onNavigate={navigate} />;
      case "memberSelection":
        return <MemberSelectionPage onNavigate={navigate} />;
      case "fieldSelection":
        return <FieldsSelectionPage onNavigate={navigate} />;
      case "confirmation":
        return <ConfirmationPage onNavigate={navigate} />;
      case "dashboard":
        return <DashboardPage onNavigate={navigate} />;
      case "memberReportOptions":
        return <MemberDataReportOptionsPage onNavigate={navigate} />;
      // Add cases for other pages here as they are built
      default:
        return <TaskSelectionPage onNavigate={navigate} />;
    }
  };

  const renderUnauthorisedCard = (message: string) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div>{message}</div>
      <Button appearance="primary" onClick={refreshRegistration}>
        Retry
      </Button>
      <Button appearance="primary" onClick={handleSignOut}>
        Sign in with different account
      </Button>
    </div>
  );

  const renderAuthenticatedBody = () => {
    const gateStatus = registrationState.status;
    if (gateStatus === "idle" || gateStatus === "loading") {
      return <Spinner label="Checking your access..." />;
    }
    if (gateStatus === "unauthorised" || gateStatus === "error") {
      return renderUnauthorisedCard(
        registrationState.errorMessage ||
          "We couldn’t verify your access. Please contact your administrator."
      );
    }
    if (isPdfLoading) {
      return <Spinner label="Loading PDF..." />;
    }
    if (pdfUrl) {
      return (
        <PdfViewer
          fileUrl={pdfUrl}
          page={pdfViewerPage}
          onClose={() => {
            // Close the viewer and return to the last content page.
            closePdfViewer();
          }}
        />
      );
    }
    return renderCurrentPage();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", padding: "16px" }}>
      <Header
        logo={require("./../../../assets/sstone-logo-v2-128.png")}
        title="Sourcestone"
        onSignOut={handleSignOut}
        onGoToDashboard={handleGoToDashboard}
      />
      <div
        style={{
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          paddingTop: "24px",
        }}
      >
        <Dialog
          open={Boolean(pdfError)}
          onOpenChange={(_, data) => {
            if (!data.open) {
              setPdfError(null);
            }
          }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>PDF unavailable</DialogTitle>
              <DialogContent>{pdfError}</DialogContent>
              <DialogActions>
                <Button appearance="primary" onClick={() => setPdfError(null)}>
                  Got it
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
        {/* Inactivity warning — shown when the idle timer fires. */}
        <IdleWarningDialog
          isOpen={isWarning}
          remainingSeconds={remainingSeconds}
          onStayLoggedIn={resetIdleTimer}
          onLogout={handleSignOut}
        />
        {isAuthenticated ? renderAuthenticatedBody() : <LoginPage />}
      </div>
    </div>
  );
};

const App = () => {
  const styles = useAppStyles();
  // Security Gate: Ensure the application is running inside a valid Office host (e.g. Excel).
  // This prevents users from running the add-in directly in a web browser (e.g. Chrome/Edge)
  // where they could potentially try to probe the API.
  //
  // Office.onReady() in index.tsx ensures Office is initialized before we render.
  //
  // CRITICAL EXCEPTION FOR AUTHENTICATION:
  // When MSAL performs a popup login or redirect, it may reload the page or open a popup
  // that DOES NOT have the Office context (it is just a pure browser window).
  // If we block execution there, the auth flow will break (the popup won't close).
  //
  // Detection logic:
  // 1. Check for Office host (primary check).
  // 2. Check for MSAL response hash (if the URL contains #code=... or #token=..., it's likely a redirect).
  // 3. Check for specific auth paths (if you use a dedicated auth.html, this is implicit, but if taskpane.html handles it, we need this).

  const isOfficeHost = typeof Office !== "undefined" && Office.context && Office.context.host;
  const isAuthRedirect =
    window.location.hash.includes("code=") || window.location.hash.includes("error=");

  // We allow the app to render if it's in Excel OR if it's currently handling an Auth callback.
  if (!isOfficeHost && !isAuthRedirect) {
    return (
      <FluentProvider className={styles.provider} theme={sourcestoneLightTheme}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <h2 style={{ marginBottom: "12px", color: "#242424" }}>Access Restricted</h2>
          <p style={{ color: "#424242" }}>
            This application is designed to run securely inside Microsoft Excel.
          </p>
          <p style={{ color: "#616161", fontSize: "0.9em" }}>
            Please open the add-in via the Excel ribbon.
          </p>
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider className={styles.provider} theme={sourcestoneLightTheme}>
      <RegistrationProvider>
        <JobProvider>
          <AppContent />
        </JobProvider>
      </RegistrationProvider>
    </FluentProvider>
  );
};

export default App;
